import { Building2, ListChecks, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { announceAttritionCategoriesChanged } from '../../../../lib/navEvents';
import { ApiError } from '../../../../lib/apiError';
import { attritionCategoriesApi } from '../attritionCategoriesApi';
import {
  ATTRITION_CATEGORY_AUDIT_ENTITY_TYPES,
  type AttritionCategoryDetail,
  type AttritionCategoryListItem,
} from '../attritionCategoryTypes';
import { AttritionCategoryModal } from '../components/AttritionCategoryModal';

/**
 * The attrition categories — Retirement, Resignation, Illness, and whatever the organization adds.
 *
 * The anchor opens the **profile**, where the checklist and its reviewers are read; Edit is in the
 * row menu and needs the detail, so it fetches it first — the list row does not carry the lines.
 */
export function AttritionCategoriesIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const [editing, setEditing] = useState<{ detail: AttritionCategoryDetail; section: 'details' | 'checklist' | 'clearances' } | null>(
    null,
  );
  const [creating, setCreating] = useState(false);
  const [confirming, setConfirming] = useState<{
    kind: 'deactivate' | 'activate';
    category: AttritionCategoryListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchCategories = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      attritionCategoriesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  /*
    Each section has its own edit, so the row offers all three. Each fetches the DETAIL first — the
    list row does not carry the lines — and a failed fetch has no form to report in, so it toasts.
  */
  const openSection = useCallback(
    (category: AttritionCategoryListItem, section: 'details' | 'checklist' | 'clearances') => {
      attritionCategoriesApi
        .get(category.id)
        .then((detail) => setEditing({ detail, section }))
        .catch((caught: unknown) =>
          toast.failure(caught instanceof ApiError ? caught.message : 'Could not load this category.'),
        );
    },
    [toast],
  );

  const rowActions = useCallback(
    (category: AttritionCategoryListItem): MenuItem[] => [
      {
        key: `edit-${category.id}`,
        label: 'Edit',
        permission: 'attrition-categories.update',
        onClick: () => openSection(category, 'details'),
      },
      {
        key: `edit-checklist-${category.id}`,
        label: 'Edit checklist',
        icon: <ListChecks className="h-4 w-4" aria-hidden="true" />,
        permission: 'attrition-categories.update',
        onClick: () => openSection(category, 'checklist'),
      },
      {
        key: `edit-clearances-${category.id}`,
        label: 'Edit clearances',
        icon: <Building2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'attrition-categories.update',
        onClick: () => openSection(category, 'clearances'),
      },
      {
        key: `restore-${category.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'attrition-categories.delete',
        disabledReason: category.isActive ? 'This category is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', category }),
      },
    ],
    [openSection],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await attritionCategoriesApi.deactivate(confirming.category.id);
      else await attritionCategoriesApi.activate(confirming.category.id);

      announceAttritionCategoriesChanged();

      toast.success(`${confirming.category.name} was ${confirming.kind === 'deactivate' ? 'deleted' : 'restored'}.`);
      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<AttritionCategoryListItem>
        module="attrition-categories"
        // Reached through Attrition → Settings, so Settings links: that crumb is the way back.
        breadcrumb={[
          'Human Resources',
          'Attrition',
          { label: 'Settings', to: '/attrition/settings' },
          'Categories',
        ]}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={ATTRITION_CATEGORY_AUDIT_ENTITY_TYPES}
        rowKey={(category) => category.id}
        fetch={fetchCategories}
        reloadKey={reloadKey}
        searchPlaceholder="Search categories or the documents they ask for"
        emptyMessage="No category yet. Add the kinds of separation — Retirement, Resignation, Illness — with the documents each needs."
        onCreate={() => setCreating(true)}
        createPermission="attrition-categories.create"
        onRowOpen={(category) => navigate(`/attrition/settings/categories/${category.id}`)}
        openPermission="attrition-categories.read"
        rowActions={rowActions}
        onDelete={(category) => setConfirming({ kind: 'deactivate', category })}
        deletePermission="attrition-categories.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(category) => (category.isActive ? null : 'This category is already deleted.')}
        onClearData={async () => {
          const cleared = await attritionCategoriesApi.clear();
          announceAttritionCategoriesChanged();
          toast.success(`${cleared} ${cleared === 1 ? 'category was' : 'categories were'} deleted.`);
          return cleared;
        }}
        clearDataPermission="attrition-categories.clear-data"
        moduleNoun="attrition categories"
        otherInfo={(category) => category}
      />

      {creating || editing !== null ? (
        <AttritionCategoryModal
          open
          section={editing?.section ?? 'create'}
          editing={editing?.detail ?? null}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={(_saved, options) => {
            refresh();
            // Add another keeps the create form up for the next one; the toast is the modal's.
            if (options?.keepOpen) return;
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete category' : 'Restore category'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`attrition-category-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.category.name}" stops being offered when a request is filed.`
                  : `"${confirming.category.name}" is offered again when a request is filed.`}
              </p>

              {confirming.kind === 'deactivate' && confirming.category.requestCount > 0 ? (
                <p>
                  {confirming.category.requestCount} request
                  {confirming.category.requestCount === 1 ? ' is' : 's are'} filed under it. They keep
                  naming it, and their documents can still be uploaded and reviewed.
                </p>
              ) : null}

              <p>Nothing is erased — a deleted category can be restored from this list.</p>

              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<AttritionCategoryListItem>[] = [
  {
    key: 'name',
    header: 'Name',
    width: 260,
    flex: true,
    sortable: true,
    render: (category) => <TextCell value={category.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 360,
    summary: true,
    render: (category) => <TextCell value={category.description} />,
  },
  {
    key: 'documentCount',
    header: 'Documents',
    width: 120,
    align: 'right',
    summary: true,
    render: (category) => <TextCell value={String(category.documentCount)} mono numeric />,
  },
  {
    key: 'clearanceCount',
    header: 'Clearances',
    width: 120,
    align: 'right',
    render: (category) => <TextCell value={String(category.clearanceCount)} mono numeric />,
  },
  {
    key: 'requestCount',
    header: 'Requests',
    width: 110,
    align: 'right',
    // The count is of another module's records; the name and checklist beside it are this module's own.
    permission: 'attrition-requests.read',
    render: (category) => <TextCell value={String(category.requestCount)} mono numeric />,
  },
  {
    // The stored sort value, under a heading that says so — a paged table never holds the whole ladder.
    key: 'sortOrder',
    header: 'Order',
    width: 90,
    align: 'right',
    sortable: true,
    render: (category) => <TextCell value={String(category.sortOrder)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (category) =>
      category.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
